import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { aiRewardGenerator } from "@/lib/ai";

const CURRENCIES = [{ code: "INR", symbol: "₹" }, { code: "USD", symbol: "$" }, { code: "EUR", symbol: "€" }, { code: "GBP", symbol: "£" }];

const RewardGenerator = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [form, setForm] = useState({ product_name: "", category: "technology", tagline: "", story_snippet: "", unit_cost: 800, target_market: "", currency: "INR", goal: 500000 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  if (!authLoading && !user) { requireAuth("Sign in to generate reward tiers."); }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cur = CURRENCIES.find((c) => c.code === form.currency)?.symbol || "";

  const run = async () => {
    if (!form.product_name.trim()) { toast.error("Product name is required"); return; }
    setLoading(true);
    try {
      const r = await aiRewardGenerator(form);
      setResult(r.data);
    } catch (e) { toast.error(e.message || "Failed"); } finally { setLoading(false); }
  };

  const copyTier = (t, i) => {
    const txt = `${t.title} — ${cur}${(t.price || 0).toLocaleString()}\n${t.description}\n\nIncluded:\n${(t.included || []).map((x) => `• ${x}`).join("\n")}`;
    navigator.clipboard.writeText(txt).then(() => { setCopiedIdx(i); setTimeout(() => setCopiedIdx(null), 1500); toast.success("Copied"); });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="reward-generator-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-saffron-700"><Sparkles className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Reward Generator</span></div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Craft a 5-tier reward ladder in seconds.</h1>
          <p className="mt-2 text-navy-600 max-w-2xl">Claude designs psychology-driven pricing anchored to your product economics.</p>
        </div>
        <button onClick={run} disabled={loading} data-testid="reward-gen-run" className="btn-saffron inline-flex items-center gap-2 rounded-2xl h-11 px-5 font-semibold disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Designing…" : "Generate tiers"}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 rounded-2xl border border-warm-200 bg-white p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Product name *</label>
            <input value={form.product_name} onChange={(e) => set("product_name", e.target.value)} placeholder="AquaSense" data-testid="rg-name" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Tagline</label>
            <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Smart hydration tracker" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Short story</label>
            <textarea value={form.story_snippet} onChange={(e) => set("story_snippet", e.target.value)} rows={3} placeholder="A one-paragraph pitch" className="mt-1.5 w-full rounded-2xl border border-warm-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Unit cost</label>
              <input type="number" value={form.unit_cost} onChange={(e) => set("unit_cost", Number(e.target.value))} data-testid="rg-cost" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Currency</label>
              <select value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="rg-currency" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition">
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Funding goal</label>
            <input type="number" value={form.goal} onChange={(e) => set("goal", Number(e.target.value))} className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>
        </div>

        <div className="lg:col-span-8 space-y-4">
          {!result && loading && (<div className="rounded-2xl border border-warm-200 bg-white p-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" /><p className="mt-3 text-navy-500">Designing your ladder…</p></div>)}
          {!result && !loading && (<div className="rounded-2xl border border-warm-200 bg-cream-100 p-10 text-center text-sm text-navy-600">Fill in the fields on the left and click <span className="font-semibold text-saffron-700">Generate tiers</span>.</div>)}
          {result && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="rg-tiers">
                {(result.tiers || []).map((t, i) => (
                  <div key={i} data-testid={`rg-tier-${i}`} className="rounded-2xl border border-warm-200 bg-white p-5 flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-saffron-700">Tier {t.position}</div>
                      {t.badge && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-navy-800 text-white px-2 py-0.5">{t.badge}</span>}
                    </div>
                    <div className="mt-2 font-display text-xl font-bold text-navy-800 tracking-tight">{t.title}</div>
                    <div className="mt-1 font-display text-2xl font-bold text-saffron-600">{cur}{Number(t.price || 0).toLocaleString()}</div>
                    <p className="mt-2 text-sm text-navy-700 leading-[1.6]">{t.description}</p>
                    {(t.included || []).length > 0 && (<ul className="mt-3 space-y-0.5 text-xs text-navy-700">{t.included.map((x, j) => <li key={j} className="flex gap-1.5"><span className="text-saffron-600">•</span>{x}</li>)}</ul>)}
                    <div className="mt-3 pt-3 border-t border-warm-100 text-[11px] text-navy-500">{t.rationale}</div>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-navy-500">
                      <span>Delivery: {t.estimated_delivery || "—"}</span>
                      {t.limit && <span className="font-semibold text-saffron-700">Limit: {t.limit}</span>}
                    </div>
                    <button onClick={() => copyTier(t, i)} className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full border border-warm-200 px-2.5 h-7 text-[11px] font-semibold text-navy-700 hover:border-saffron-300 hover:text-saffron-700">
                      {copiedIdx === i ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
                    </button>
                  </div>
                ))}
              </div>
              {result.pricing_strategy && (
                <div className="rounded-2xl border border-warm-200 bg-white p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-2">Pricing strategy</div>
                  <p className="text-sm text-navy-700 leading-[1.7]">{result.pricing_strategy}</p>
                </div>
              )}
              {result.expected_mix_pct && (
                <div className="rounded-2xl border border-warm-200 bg-white p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-3">Expected backer mix</div>
                  <div className="space-y-2">
                    {Object.entries(result.expected_mix_pct).map(([k, v]) => (
                      <div key={k}>
                        <div className="flex justify-between text-xs font-semibold text-navy-700"><span>{k.replace("tier", "Tier ")}</span><span>{v}%</span></div>
                        <div className="mt-1 h-2 rounded-full bg-warm-100 overflow-hidden"><div className="h-full bg-saffron-500" style={{ width: `${v}%` }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardGenerator;
